import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { formatDZD } from '../../utils/currency';

function AdminAnnonces() {
  const [annonces, setAnnonces] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const { accessToken: token } = useAuth();

  const fetchAnnonces = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (status) params.append('status', status);
      if (search) params.append('search', search);

      const res = await fetch(`${API_URL}/admin/annonces?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAnnonces(data.annonces || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [token, status, search]);

  useEffect(() => {
    fetchAnnonces();
  }, [fetchAnnonces]);

  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`${API_URL}/admin/annonces/${id}/status`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      fetchAnnonces(pagination.page);
    } catch (err) {
      console.error(err);
    }
  };

  const certifyAnnonce = async (id) => {
    try {
      await fetch(`${API_URL}/admin/annonces/${id}/certify`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAnnonces(pagination.page);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <h2>Gestion des annonces</h2>
        <p>{pagination.total} annonce{pagination.total > 1 ? 's' : ''} au total</p>
      </div>

      <div className="admin-toolbar">
        <input
          className="admin-search"
          placeholder="Rechercher par titre ou quartier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchAnnonces()}
        />
        <select
          className="admin-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="active">Active</option>
          <option value="pending">En attente</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="glass-card admin-table-container">
        {loading ? (
          <div style={{padding: '2rem', textAlign: 'center', color: 'var(--text-light)'}}>
            Chargement...
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Titre</th>
                <th>Quartier</th>
                <th>Prix</th>
                <th>Vendeur</th>
                <th>Statut</th>
                <th>Certifie</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {annonces.map(a => (
                <tr key={a.id}>
                  <td>#{a.id}</td>
                  <td style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {a.titre}
                  </td>
                  <td>{a.quartier}</td>
                  <td>{formatDZD(a.prix)}</td>
                  <td>{a.nom} {a.prenom}</td>
                  <td>
                    <span className={`badge badge-${a.status}`}>{a.status}</span>
                  </td>
                  <td>{a.is_certified ? '✅' : '—'}</td>
                  <td>
                    <div className="admin-actions">
                      {a.status !== 'active' && (
                        <button className="btn-action approve" onClick={() => updateStatus(a.id, 'active')}>
                          Approuver
                        </button>
                      )}
                      {a.status !== 'inactive' && (
                        <button className="btn-action reject" onClick={() => updateStatus(a.id, 'inactive')}>
                          Rejeter
                        </button>
                      )}
                      {!a.is_certified && a.status === 'active' && (
                        <button className="btn-action certify" onClick={() => certifyAnnonce(a.id)}>
                          Certifier
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {annonces.length === 0 && (
                <tr>
                  <td colSpan="8" style={{textAlign: 'center', padding: '2rem', color: 'var(--text-light)'}}>
                    Aucune annonce trouvee
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="admin-pagination">
          <button
            disabled={pagination.page <= 1}
            onClick={() => fetchAnnonces(pagination.page - 1)}
          >
            ← Precedent
          </button>
          <span>Page {pagination.page} / {pagination.pages}</span>
          <button
            disabled={pagination.page >= pagination.pages}
            onClick={() => fetchAnnonces(pagination.page + 1)}
          >
            Suivant →
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminAnnonces;
